import {
	httpClient,
	getAgencyKey,
	getApplicationHeaders,
	UserTypes,
	getUserToken,
} from "@utils";

import { CashoutType } from "@gp/hub";

import { BetSlipCashoutHistory } from "@api-types/agency/autocashout/BetSlipCashoutHistory";
import { BetSlipResult } from "@api-types/agency/bet-slip/BetSlipResult";
import { GenericResponse, PageableCollection } from "@api-types/common";
import { BetSlipExtended, BetSlipForShare } from "@api-types/agency/bet-slip";
import { TQuickBetFormRequest } from "@api-types/quick-play";
import { BetSlipCombinations } from "@api-types/bet-slip/BetSlipCombinations";
import { BetSlipPayoutResult } from "@api-types/bet-slip/BetSlipPayoutResult";
import { Filter } from "@api-types/my-bets/Filter";
import { IBetSlipRequest, IBetSlipResponse } from "@gp/slip";

class BetSlipApiService {
	static baseUrl(activeUser = UserTypes.PRIMARY) {
		return `${WEBPACK_BASE_ADDRESS}${getAgencyKey(activeUser)}/bet-slips`;
	}

	static getRequestHeaders(activeUser = UserTypes.PRIMARY) {
		const appHeaders = getApplicationHeaders(activeUser);
		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		return appHeaders;
	}

	static async findBetsOnAdditionalOffer(
		owner: UserTypes = UserTypes.PRIMARY,
		filter: {
			eventId: string;
			page: number;
			pageSize: number;
		}
	): Promise<PageableCollection<BetSlipExtended>> {
		let query = new URLSearchParams();
		query.set(
			"embed",
			"betStatus,betSlipStatus,betSlipType,betSlipOffers,betSlipSecureWinSetting"
		);
		query.set("rpp", String(filter.pageSize));
		query.set("page", String(filter.page));
		query.set("eventIds", filter.eventId);
		query.set("sort", "dateCreated|desc");
		query.set("translate", String(true));

		return await httpClient.get(
			`${this.baseUrl(owner)}/get-bet-slips-for-events?${query}`,
			this.getRequestHeaders(owner),
			{ responseType: "json" }
		);
	}

	static async findBets(
		filter: Filter | null = null,
		page: number | null = 1,
		rpp: number = 10,
		activeUser: UserTypes = UserTypes.PRIMARY,
		sort: string = "dateCreated|desc"
	): Promise<PageableCollection<BetSlipExtended>> {
		let query;
		if (filter) {
			//@ts-expect-error
			query = new URLSearchParams(filter);
		} else {
			query = new URLSearchParams();
		}
		query.set("page", String(page));
		query.set("rpp", String(rpp));
		query.set("sort", String(sort));
		query.set("translate", String(true));

		return await httpClient.get(
			`${this.baseUrl(activeUser)}?${query}`,
			this.getRequestHeaders(activeUser),
			{ responseType: "json" }
		);
	}

	static async getBet(
		slipId: string,
		embed = "",
		activeUser = UserTypes.PRIMARY
	): Promise<BetSlipExtended> {
		return await httpClient.get(
			`${this.baseUrl(
				activeUser
			)}/${slipId}?embed=${embed}&translate=true`,
			this.getRequestHeaders(activeUser),
			{ responseType: "json" }
		);
	}

	static getSystemCombinations(
		id: string,
		activeUser = UserTypes.PRIMARY
	): Promise<BetSlipCombinations[]> {
		return httpClient.get(
			`${this.baseUrl(activeUser)}/${id}/combinations`,
			this.getRequestHeaders(activeUser),
			{ responseType: "json" }
		);
	}

	static getCashoutHistory(
		id: string,
		activeUser = UserTypes.PRIMARY
	): Promise<BetSlipCashoutHistory> {
		return httpClient.get(
			`${this.baseUrl(
				activeUser
			)}/${id}/bet-slip-jacktime-cashout-history-value`,
			this.getRequestHeaders(activeUser),
			{ responseType: "json" }
		);
	}

	static putCancelBet(
		id: string,
		activeUser = UserTypes.PRIMARY
	): Promise<BetSlipResult> {
		return httpClient.put(
			`${this.baseUrl(activeUser)}/${id}/cancel`,
			null,
			this.getRequestHeaders(activeUser),
			{ responseType: "json" }
		);
	}

	static getSharedBetSlip(betSlipNumber: string): Promise<BetSlipForShare> {
		return httpClient.get(
			`${this.baseUrl()}/share/${betSlipNumber}`,
			this.getRequestHeaders(),
			{ responseType: "json" }
		);
	}

	static async cashout(
		id: string,
		calculatedCashoutAmount: number,
		cType: CashoutType,
		isChangeAccepted: boolean,
		owner: UserTypes
	): Promise<BetSlipPayoutResult> {
		const response = await httpClient.put(
			`${this.baseUrl(owner)}/${id}/cashout`,
			JSON.stringify({
				betSlipId: id,
				oldCashoutAmount: calculatedCashoutAmount,
				cType: cType,
				isChangedAmountAccepted: isChangeAccepted,
			}),
			this.getRequestHeaders(owner),
			{
				responseType: "json",
			}
		);

		return response;
	}

	static async cashoutWithQrCode(
		id: string,
		calculatedCashoutAmount: number,
		isChangeAccepted: boolean,
		code: string,
		betSlipNumber: string,
		userId: string | null,
		owner: UserTypes
	): Promise<BetSlipPayoutResult> {
		const response = await httpClient.put(
			`${this.baseUrl(owner)}/cashout-with-qrcode/${betSlipNumber}`,
			JSON.stringify({
				betSlipId: id,
				oldCashoutAmount: calculatedCashoutAmount,
				isChangedAmountAccepted: isChangeAccepted,
				code: code,
			}),

			getUserToken(owner) != null ||
				(getUserToken(owner) == null && userId == null)
				? this.getRequestHeaders(owner)
				: this.getRequestHeaders(UserTypes.PRIMARY),
			{
				responseType: "json",
			}
		);

		return response;
	}

	static async fetchQuickBet(
		data: TQuickBetFormRequest
	): Promise<GenericResponse<BetSlipResult>> {
		return await httpClient.post(
			`${this.baseUrl()}/quick-bet-new`,
			JSON.stringify(data),
			this.getRequestHeaders(),
			{
				responseType: "json",
			}
		);
	}

	static async validate(
		slip: IBetSlipRequest,
		abortController: AbortController
	): Promise<IBetSlipResponse> {
		const walletOwner = App.state.rootStore.userAuthStore.getWalletOwner(
			App.state.rootStore.userAuthStore.activeAccountTypeId
		);
		return await httpClient.post(
			`${this.baseUrl(walletOwner)}/betting-slip-validate/2`,
			JSON.stringify(slip),
			this.getRequestHeaders(walletOwner),
			{
				responseType: "json",
				signal: abortController.signal,
			}
		);
	}

	static submit(slip: IBetSlipRequest): Promise<IBetSlipResponse> {
		const walletOwner = App.state.rootStore.userAuthStore.getWalletOwner(
			App.state.rootStore.userAuthStore.activeAccountTypeId
		);

		const headers = {
			...this.getRequestHeaders(walletOwner),
			"x-rc": App.state.rootStore.userAuthStore.user?.username || "",
		};

		return httpClient.post(
			`${this.baseUrl(walletOwner)}/betting-slip-validate/4`,
			JSON.stringify(slip),
			headers,
			{
				responseType: "json",
			}
		);
	}
}

export default BetSlipApiService;
